import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import IUser from '../../../_interfaces/IUser';
import { CustomersService } from '../../../_services/customers/customers.service';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { SessionService } from '../../../_services/session/session.service';
import { UsersService } from '../../../_services/users/users.service';
import { BaseComponent } from '../../../components/base/base.component';
import { AdminStatePanelComponent } from '../../admin-ui/admin-state-panel/admin-state-panel.component';
import { AdminTableActionsComponent } from '../../admin-ui/admin-table-actions/admin-table-actions.component';
import { AdminListToolbarComponent } from '../../admin-ui/admin-list-toolbar/admin-list-toolbar.component';

@Component({
  standalone: true,
  selector: 'app-table-users',
  templateUrl: './table-users.component.html',
  styleUrls: ['./table-users.component.scss'],
  imports: [
    TableModule,
    CommonModule,
    TranslatePipe,
    ButtonModule,
    AdminTableActionsComponent,
    AdminListToolbarComponent,
    AdminStatePanelComponent,
  ],
})
export class TableUsersComponent extends BaseComponent implements OnInit {
  public users: IUser[] = [];
  public filteredUsers: IUser[] = [];
  public searchValue = '';

  constructor(
    private readonly userService: UsersService,
    private readonly customerService: CustomersService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    public readonly translate: TranslateService,
    private readonly errorsService: ErrorsService,
    public readonly session: SessionService,
    public readonly router: Router,
  ) {
    super(session);
  }

  onSearch(value: string): void {
    this.searchValue = value;
    const query = value.trim().toLowerCase();

    if (!query) {
      this.filteredUsers = [...this.users];
      return;
    }

    this.filteredUsers = this.users.filter((user) =>
      [user.name, user.surname, user.email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }

  public isAdmin(user: IUser): boolean {
    return !!user?.isAdmin;
  }

  public canDelete(user: IUser): boolean {
    if (!user?.id) {
      return false;
    }

    if (+user.id === +this.users_id) {
      return false;
    }

    return !user.isPrimaryAdmin;
  }

  confirmDelete(user: IUser): void {
    if (!user.id || !this.canDelete(user)) {
      return;
    }

    this.confirmationService.confirm({
      header: this.translate.instant('ADMIN.USERS.CONFIRM.DELETE.TITLE', {
        user: user.email || user.name,
      }),
      icon: 'pi pi-exclamation-triangle',
      message: this.translate.instant('ADMIN.USERS.CONFIRM.DELETE.TEXT', {
        user: user.email || user.name,
      }),
      accept: () => this.delete(user),
    });
  }

  private delete(user: IUser): void {
    this.userService.delete(this.customers_id, user.id).subscribe({
      next: () => {
        this.messageService.add({
          summary: this.translate.instant('MESSAGES.USERS.DELETE.SUMMARY'),
          detail: this.translate.instant('MESSAGES.USERS.DELETE.DETAIL', {
            user: user.email,
          }),
          severity: 'success',
        });

        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.somethingWentWrong(err);
      },
    });
  }

  load(): void {
    this.customerService.getUsers(this.customers_id).subscribe({
      next: (users: IUser[]) => {
        this.users = users;
        this.onSearch(this.searchValue);
      },
      error: (err: HttpErrorResponse) => {
        this.somethingWentWrong(err);
      },
    });
  }

  somethingWentWrong(err: HttpErrorResponse): void {
    const code = this.errorsService.getMessageFromUserError(err);
    this.messageService.add({
      summary: this.translate.instant('MESSAGES.USERS.ERROR.SUMMARY'),
      detail: this.translate.instant(`MESSAGES.USERS.ERROR.${code}`),
      severity: 'error',
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.load();
  }
}
